import { useEffect, useState } from 'react';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { getDb } from '@talk2nick/shared';
import type { User } from '@talk2nick/core';

/**
 * 여러 사용자 프로필 조회 (대화방 목록의 상대 이름/아바타용).
 *
 * PLAN 3장은 DM 제목을 "상대 닉네임 실시간 계산"으로 두지만, 목록에서 상대마다
 * onSnapshot을 걸면 읽기 비용이 인원수에 비례해 늘어난다. 그래서 목록은 세션당
 * 1회 `getDocs(where(documentId(), 'in', chunk))`로 30개씩 묶어 가져와 모듈 캐시에 담고,
 * 상세 화면만 실시간 구독한다 (PLAN v0.2 Phase 4).
 */
const cache = new Map<string, User>();
/** uid별 진행 중인 조회 — 다른 훅 인스턴스가 같은 uid를 기다릴 수 있게 Promise를 공유한다 */
const inFlight = new Map<string, Promise<void>>();
const FETCH_RETRIES = 2;

async function fetchChunk(chunk: string[]): Promise<void> {
  for (let attempt = 0; attempt <= FETCH_RETRIES; attempt++) {
    try {
      const snap = await getDocs(
        query(collection(getDb(), 'users'), where(documentId(), 'in', chunk)),
      );
      for (const d of snap.docs) cache.set(d.id, d.data() as User);
      return;
    } catch (error) {
      console.error('[talk2nIcK] 프로필 일괄 조회 실패:', error);
      // 일시적 네트워크/인증 지연은 짧게 재시도한다 (실패가 캐시에 고착되면 '?'로 남는다)
      if (attempt < FETCH_RETRIES) await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
    }
  }
}

export function useUserProfiles(uids: string[]): Map<string, User> {
  const key = [...uids].sort().join(',');
  const [profiles, setProfiles] = useState<Map<string, User>>(() => snapshot(uids));

  useEffect(() => {
    const targets = key === '' ? [] : key.split(',');
    let cancelled = false;
    // 이미 캐시에 있는 uid도 state에 반영한다 (key가 바뀌어도 fetch가 없으면 갱신이 누락됐다)
    setProfiles(snapshot(targets));

    const missing = targets.filter((uid) => !cache.has(uid) && !inFlight.has(uid));
    const CHUNK_SIZE = 30;
    for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
      const chunk = missing.slice(i, i + CHUNK_SIZE);
      const p = fetchChunk(chunk).finally(() => {
        for (const uid of chunk) inFlight.delete(uid);
      });
      for (const uid of chunk) inFlight.set(uid, p);
    }

    const pending = new Set(targets.map((uid) => inFlight.get(uid)).filter((p) => p !== undefined));
    if (pending.size > 0) {
      void Promise.all(pending).then(() => {
        if (!cancelled) setProfiles(snapshot(targets));
      });
    }

    return () => {
      cancelled = true;
    };
    // key(정렬된 uid 목록)만 의존한다 — 배열 참조가 매 렌더 바뀌어도 재조회하지 않는다
  }, [key]);

  return profiles;
}

function snapshot(uids: string[]): Map<string, User> {
  const next = new Map<string, User>();
  for (const uid of uids) {
    const profile = cache.get(uid);
    if (profile) next.set(uid, profile);
  }
  return next;
}

export function updateUserProfileCache(uid: string, partial: Partial<User>): void {
  const current = cache.get(uid);
  if (current) {
    cache.set(uid, { ...current, ...partial });
  }
}
